/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { normalizeBatteryPercentage, roundSpeed } from '../../../../utils';
import { EPlotType, IEcmDiagnosticError, IVehicleDiagnosticsResponse } from '../../../core/models';
import { BalloonService } from '../../../core/services';
import {
  BalloonClosedByUser,
  CloseSidePanel,
  EBalloonInfoActions,
  ELayoutActions,
  EMapsActions,
  OpenEcmPanel,
  OpenInsightsPanel,
  SetSelectedMarkerSuccess
} from '../../../core/store/actions';
import { CloseEcmPanel, LoadDiagnosticsError, LoadDiagnosticsSuccess } from '../../../core/store/actions/ecm-panel.actions';
import { getEcmPanelVehicleId, getIsEcmEnabledFeatureToggleEnabled, getIsEcmPanelOpen } from '../../../core/store/selectors';
import { IAppState } from '../../../core/store/state';

/**
 * The diagnostics API returns the traction battery charge as a 0..1 fraction (e.g. "0.89"), but the panel's
 * battery widget (fm-ev-battery-section) expects a 0..100 percentage. Normalise it to a percentage string so
 * it matches the socket mapper convention (which already multiplies the charge level by 100). Non-numeric
 * values (e.g. "--") are left untouched.
 */
export function normalizeDiagnosticsBatteryScale(response: IVehicleDiagnosticsResponse): IVehicleDiagnosticsResponse {
  if (!response?.tractionBattery) {
    return response;
  }
  const raw = Number(response.tractionBattery.value);
  // Non-numeric readings (e.g. "--") are left untouched. Numeric values are approximated by the shared
  // helper so the panel and the map balloon scale/round the battery identically.
  if (!Number.isFinite(raw)) {
    return response;
  }
  return {
    ...response,
    tractionBattery: {
      ...response.tractionBattery,
      value: String(normalizeBatteryPercentage(raw))
    }
  };
}

/**
 * The diagnostics API may return the fuel level as a decimal string (e.g. "64.7"), but the panel expects
 * it rounded to the nearest integer to match the socket mapper and balloon display conventions.
 * Non-numeric values (e.g. "--") are left untouched.
 */
export function normalizeDiagnosticsFuelRounding(response: IVehicleDiagnosticsResponse): IVehicleDiagnosticsResponse {
  if (!response?.fuel) {
    return response;
  }
  const fuelValue = Number(response.fuel.value);
  if (!Number.isFinite(fuelValue)) {
    return response;
  }
  return {
    ...response,
    fuel: {
      ...response.fuel,
      value: String(Math.round(fuelValue))
    }
  };
}

/**
 * The diagnostics API returns the speed already converted to the user's units but with decimals
 * (e.g. "31.0686"). The map speed display rounds speed to the nearest whole number
 * (PlotFormattingService.formatSpeed), and the socket mapper does the same, so round the HTTP speed
 * identically via the shared helper. Non-numeric values (e.g. "--") are left untouched.
 */
export function normalizeDiagnosticsSpeedRounding(response: IVehicleDiagnosticsResponse): IVehicleDiagnosticsResponse {
  if (!response?.speed) {
    return response;
  }
  const speedValue = Number(response.speed.value);
  if (!Number.isFinite(speedValue)) {
    return response;
  }
  return {
    ...response,
    speed: {
      ...response.speed,
      value: String(roundSpeed(speedValue))
    }
  };
}

@Injectable()
export class EcmPanelEffects {
  // Opening the panel (or switching vehicle) re-triggers the load; switchMap cancels any in-flight request.
  public loadDiagnosticsOnPanelOpen$ = createEffect(() =>
    this._actions$.pipe(
      ofType<OpenEcmPanel>(ELayoutActions.OpenEcmPanel),
      switchMap(action =>
        this._balloonService.getVehicleDiagnostics(action.payload.vehicleId).pipe(
          map(response => {
            const normalized = normalizeDiagnosticsBatteryScale(response);
            const withRoundedFuel = normalizeDiagnosticsFuelRounding(normalized);
            const withRoundedSpeed = normalizeDiagnosticsSpeedRounding(withRoundedFuel);
            return new LoadDiagnosticsSuccess(withRoundedSpeed);
          }),
          catchError(error => of(new LoadDiagnosticsError({ errors: this._toDiagnosticErrors(error) })))
        )
      )
    )
  );

  /**
   * Keeps the ECM panel in sync with the currently selected object while it is open. Both a map balloon/marker
   * click and a left-list selection funnel through `SetSelectedMarkerSuccess`. It only runs when the ECM feature
   * toggle is enabled and the panel is open. While the panel is open:
   *  - selecting a DIFFERENT vehicle or powered asset reloads the panel for that vehicle;
   *  - selecting an object that has no diagnostics panel (non-powered asset, place, geofence, ...) closes the panel;
   *  - selecting the same vehicle again is a no-op.
   */
  public syncEcmPanelWithSelectedMarker$ = createEffect(() =>
    this._actions$.pipe(
      ofType<SetSelectedMarkerSuccess>(EMapsActions.SetSelectedMarkerSuccess),
      map(action => action.payload),
      withLatestFrom(
        this._store.pipe(select(getIsEcmEnabledFeatureToggleEnabled)),
        this._store.pipe(select(getIsEcmPanelOpen)),
        this._store.pipe(select(getEcmPanelVehicleId))
      ),
      // Only sync when the ECM feature is enabled for the user and the panel is currently open.
      filter(([, isEcmFeatureEnabled, isPanelOpen]) => isEcmFeatureEnabled && isPanelOpen),
      mergeMap(([marker, , , currentVehicleId]) => {
        // The diagnostics panel can only be opened for vehicles and powered assets.
        const canAccessDiagnosticsPanel = marker.type === EPlotType.vehicle || marker.type === EPlotType.poweredAsset;

        // Selecting an object without a diagnostics panel closes it (reset the slice, hide the side panel, restore insights).
        if (!canAccessDiagnosticsPanel) {
          return [new CloseEcmPanel(), new CloseSidePanel(), new OpenInsightsPanel()];
        }

        // A different vehicle/powered asset reloads the diagnostics; the same one leaves the panel untouched.
        if (marker.id !== currentVehicleId) {
          return [new OpenEcmPanel({ vehicleName: marker.title, vehicleId: marker.id, plotType: marker.type })];
        }

        return [];
      })
    )
  );

  /**
   * When the user explicitly closes the map balloon (its X), also dismiss the ECM diagnostics side panel.
   * This reacts to `BalloonClosedByUser` rather than the generic `CloseBalloon`, so programmatic closes
   * (e.g. marker re-plotting while switching vehicles) do NOT tear the panel down.
   */
  public closeEcmPanelOnUserBalloonClose$ = createEffect(() =>
    this._actions$.pipe(
      ofType<BalloonClosedByUser>(EBalloonInfoActions.BalloonClosedByUser),
      withLatestFrom(this._store.pipe(select(getIsEcmPanelOpen))),
      filter(([, isEcmPanelOpen]) => isEcmPanelOpen),
      mergeMap(() => [new CloseEcmPanel(), new CloseSidePanel(), new OpenInsightsPanel()])
    )
  );

  constructor(
    private readonly _actions$: Actions,
    private readonly _balloonService: BalloonService,
    private readonly _store: Store<IAppState>
  ) {}

  /**
   * Normalises a failed diagnostics response into the structured error array the panel state expects.
   * When the backend supplies its own array of `{ code, error, description }` it is passed through;
   * otherwise the HTTP error is mapped to a single entry.
   */
  private _toDiagnosticErrors(error: any): IEcmDiagnosticError[] {
    if (Array.isArray(error?.error)) {
      return error.error as IEcmDiagnosticError[];
    }

    return [
      {
        code: error?.status == null ? 'UNKNOWN' : String(error.status),
        error: error?.statusText || error?.name || 'Error',
        description: error?.message || 'Failed to load diagnostics.'
      }
    ];
  }
}
